import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Input, RadioGroup, Sheet, InlineAlert } from '../../../design-system/ui';
import { SITUATION_KINDS, type SituationKind } from '../../../types/journey';
import { usePlusEnabled } from '../plus/plus-flag';
import { useJourneyNudge } from './JourneyNudgeCard';
import { saveSituation } from './journey-api';
import { journeyMessages as m } from './journey-messages';

const OPTION_MESSAGE = {
  in_work: m.inWork,
  in_work_looking: m.inWorkLooking,
  under_notice: m.underNotice,
  out_of_work: m.outOfWork,
  student_graduate: m.studentGraduate,
  new_to_finland: m.newToFinland,
} as const;

/**
 * SET-1: the situation question, asked on the second page view of the first signed-in visit, never
 * inferred and always skippable. Skipping tells the server "not now" (a skipped question only comes
 * back as contextual asks). The sheet opens only when the engine answers with SET-1.
 */
export function SituationSheet() {
  const intl = useIntl();
  const plusEnabled = usePlusEnabled();
  const { nudge, acted, dismiss } = useJourneyNudge('welcome');
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<SituationKind | null>(null);
  const [noticeEnd, setNoticeEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const shown = useRef(false);

  useEffect(() => {
    if (nudge?.ruleId === 'SET-1' && !shown.current) {
      shown.current = true;
      setOpen(true);
    }
  }, [nudge]);

  if (!plusEnabled || !nudge || nudge.ruleId !== 'SET-1') return null;
  const canSave = kind !== null && (kind !== 'under_notice' || /^\d{4}-\d{2}-\d{2}$/.test(noticeEnd));

  const onSave = async () => {
    if (!kind) return;
    setSaving(true);
    setFailed(false);
    const result = await saveSituation({ kind, ...(kind === 'under_notice' ? { noticeEnd } : {}) });
    setSaving(false);
    if (!result) {
      setFailed(true);
      return;
    }
    acted();
    setOpen(false);
  };

  const skip = () => {
    setOpen(false);
    dismiss();
  };

  return (
    <Sheet
      open={open}
      onClose={skip}
      title={intl.formatMessage(m.situationTitle)}
      closeLabel={intl.formatMessage(m.situationSkip)}
      footer={
        <div className="flex items-center gap-2">
          <Button onClick={onSave} disabled={!canSave || saving}>
            {intl.formatMessage(m.situationSave)}
          </Button>
          <Button variant="tertiary" onClick={skip}>
            {intl.formatMessage(m.situationSkip)}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="situation-sheet">
        <p className="m-0 type-body text-jc-ink-secondary">{intl.formatMessage(m.situationBody)}</p>
        <RadioGroup
          legend={intl.formatMessage(m.situationTitle)}
          legendHidden
          value={kind ?? ''}
          onValueChange={(v) => setKind(v as SituationKind)}
          options={SITUATION_KINDS.map((k) => ({ value: k, label: intl.formatMessage(OPTION_MESSAGE[k]) }))}
        />
        {kind === 'under_notice' ? (
          <Input type="date" label={intl.formatMessage(m.situationNoticeEnd)} value={noticeEnd} onChange={(e) => setNoticeEnd(e.target.value)} />
        ) : null}
        {failed ? <InlineAlert tone="attention" title={intl.formatMessage(m.situationError)} /> : null}
      </div>
    </Sheet>
  );
}
